import Konva from "konva";
import { Arrow, Group, Label, Tag, Text } from "react-konva";
import { useTheme } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import {
  selectIsGradingViewActive,
  selectViewedGroupEdges,
  selectViewedGroups,
  selectViewedRectangles,
} from "@store/selectors";
import { openGroupEdgeContextMenu } from "@features/annogram/canvas/state/canvasAuxSlice";
import {
  addMargin,
  dashStateForCredence,
  rectsHaveIntersection,
} from "@shared/utils/utils";
import { calculateArrowPoints } from "./CanvasArrow";
import { groupBounds } from "./CanvasGroupFrame";
import type { RectangleGeometry } from "@/types/canvasTypes";

/**
 * Renders group-level edges: connections from a group frame to a rect
 * or to another group frame. Same visual language as rect arrows
 * (kind → pointers, credence → dash), same right-click affordance.
 * Purely semantic — these edges never feed the Parsons checks.
 */
const GroupEdgeLayer = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const edges = useAppSelector(selectViewedGroupEdges);
  const groups = useAppSelector(selectViewedGroups);
  const rectangles = useAppSelector(selectViewedRectangles);
  const readOnly = useAppSelector(selectIsGradingViewActive);

  if (edges.length === 0) return null;
  const byId = new Map(groups.map((g) => [g.id, g]));

  return (
    <>
      {edges.map((edge, i) => {
        const fromGroup = byId.get(edge.fromGroupId);
        if (!fromGroup) return null;
        const from = groupBounds(fromGroup, rectangles);
        if (!from) return null;

        let to: RectangleGeometry | null = null;
        if (edge.toGroupId !== undefined) {
          const g = byId.get(edge.toGroupId);
          to = g ? groupBounds(g, rectangles) : null;
        } else if (edge.toRectIdx !== undefined) {
          to = rectangles[edge.toRectIdx] ?? null;
        }
        if (!to) return null;
        // Same near-touching guard as CanvasArrow: compare the
        // margin-expanded boxes so no hidden stub gets drawn.
        const fromBox = addMargin(from, 5);
        const toBox = addMargin(to, 5);
        if (rectsHaveIntersection(fromBox, toBox)) return null;

        let [startX, startY, endX, endY] = calculateArrowPoints(fromBox, toBox);
        // Opposing pair (A->B and B->A, group targets): shift both
        // endpoints perpendicular to the line so the two edges render
        // as parallel offset lines — same treatment as rect arrows.
        const hasSibling =
          edge.toGroupId !== undefined &&
          edges.some(
            (e2) =>
              e2.fromGroupId === edge.toGroupId &&
              e2.toGroupId === edge.fromGroupId
          );
        if (hasSibling) {
          const lineDx = endX - startX;
          const lineDy = endY - startY;
          const len = Math.sqrt(lineDx * lineDx + lineDy * lineDy);
          if (len > 0) {
            const magnitude = 7;
            startX += (-lineDy / len) * magnitude;
            startY += (lineDx / len) * magnitude;
            endX += (-lineDy / len) * magnitude;
            endY += (lineDx / len) * magnitude;
          }
        }
        const dashState = dashStateForCredence(edge.credence);
        const handleContextMenu = (e: Konva.KonvaEventObject<MouseEvent>) => {
          if (readOnly) return;
          e.evt.preventDefault();
          dispatch(
            openGroupEdgeContextMenu({
              fromGroupId: edge.fromGroupId,
              toGroupId: edge.toGroupId,
              toRectIdx: edge.toRectIdx,
            })
          );
        };
        return (
          <Group
            key={`${edge.fromGroupId}->${edge.toGroupId ?? edge.toRectIdx}-${i}`}
            onContextMenu={handleContextMenu}
          >
            <Arrow
              points={[startX, startY, endX, endY]}
              stroke={theme.palette.text.primary}
              strokeWidth={edge.kind === "bidirectional" ? 2.5 : 1.5}
              fill={theme.palette.text.primary}
              pointerAtEnding={edge.kind !== "undirected"}
              pointerAtBeginning={edge.kind === "bidirectional"}
              dashEnabled={dashState.enabled}
              dash={dashState.pattern}
              hitStrokeWidth={30}
            />
            {edge.tag ? (
              <Label x={(startX + endX) / 2} y={(startY + endY) / 2 - 5}>
                <Tag fill={theme.palette.background.default} opacity={1} />
                <Text
                  text={edge.tag}
                  wrap="none"
                  fill={theme.palette.text.primary}
                />
              </Label>
            ) : null}
          </Group>
        );
      })}
    </>
  );
};

export default GroupEdgeLayer;
